import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTeamInfo } from '../../../../app/hooks/useTeamInfo'
import { formatMoneyValue } from '../../../../app/utils/formatMoneyValue'
import { GoalsValidation } from '@/view/components/GoalsForm'
import { formatPhoneNumber } from '@/app/utils/formatPhoneNumber'

const GoalsObjectValidation = z.record(z.string(), GoalsValidation)

const schema = z.object({
  name: z.string().optional(),
  about: z.string().optional(),
  supportPhone: z
    .string()
    .optional()
    .transform((f) => (f ? '55' + f.replace(/\D/g, '') : undefined))
    .refine(
      (f) => (f ? f.length > 11 && f.length < 14 : !f),
      'Telefone inválido',
    ),
  goal: GoalsValidation,
  goals: z.union([GoalsValidation, GoalsObjectValidation]).optional(),
})

export type FormEditTeamData = z.infer<typeof schema>

export const useTeamsDetailsPageController = () => {
  const { id } = useParams()
  const methods = useForm<FormEditTeamData>({
    resolver: zodResolver(schema),
  })

  const { data, isFetching, isLoading, refetch } = useTeamInfo(id!)

  const { reset } = methods

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        supportPhone: data?.supportPhone
          ? formatPhoneNumber(data.supportPhone.slice(2))
          : '',
        goal: data.goal?.length
          ? data.goal.map((goal) => ({
              value: formatMoneyValue(goal?.value || 0) as unknown as number,
              commission: (goal?.commission || 0).toFixed(
                2,
              ) as unknown as number,
              bonus: formatMoneyValue(goal?.bonus || 0) as unknown as number,
            }))
          : [],
      })
    }
  }, [reset, data])

  return {
    data,
    isFetching,
    isLoading,
    methods,
    refetch,
  }
}
